// External modules
import type { ReactNode } from 'react';
import React from 'react';
import { useStoreState } from '@/store';
// @ts-expect-error untyped import
import { FieldError } from '@checkout/react-components';

// Internal modules
import { Checkbox } from '@/components/Common';
import { getContent } from '@/lib';
import { useMountLogging } from '@/lib/reactHooks';
import { errorStyleOverrides } from './styles';
import { logBlurOrFocusEvent } from '../Form/utils';
import type { AppMode, InputOnChange } from '@/types';
import { APP_MODE } from '@/types';
import { SIGNUP_AGREE_TO_TERMS } from '@/lib/features/constants';

interface OnboardTermsWithCheckboxProps {
  changeHandler: InputOnChange;
  errorContentKey?: string;
  label?: ReactNode;
  type: AppMode;
}

export const OnboardTermsWithCheckbox = ({
  changeHandler,
  type,
  label,
  errorContentKey = 'fieldErrors.signupTermsMissing',
}: OnboardTermsWithCheckboxProps): JSX.Element => {
  useMountLogging(`${type}OnboardTermsCheckbox`);
  const checkboxName =
    type === APP_MODE.GUEST ? 'guestAgreeToTerms' : SIGNUP_AGREE_TO_TERMS;
  const isSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const agreeToTerms = useStoreState(({ form }) => form[checkboxName]);
  const hasError = Boolean(agreeToTerms?.displayError);

  return (
    <>
      <Checkbox
        aria-describedby={hasError ? `${checkboxName}-error` : null}
        aria-invalid={hasError}
        blurHandler={logBlurOrFocusEvent}
        changeHandler={changeHandler}
        checked={agreeToTerms?.value}
        disabled={isSubmitting}
        focusHandler={logBlurOrFocusEvent}
        id={checkboxName}
        label={label}
        name={checkboxName}
        required={true}
        isTermsCheckbox
        testID={checkboxName}
        error={agreeToTerms?.error}
      />

      {hasError && (
        <FieldError
          id="agreeToTerms-error"
          styleOverrides={errorStyleOverrides}
        >
          {getContent(errorContentKey)}
        </FieldError>
      )}
    </>
  );
};
